import type { JSX, ReactNode } from 'react'

// The pipeline control that rides on every forecast-aware chart, plus the
// static badge for the charts no plan or pipeline can reach.
//
// WHAT IT SWITCHES. Weighted pipeline: deals not yet signed, at the sheet's FCF
// conversion rate (charts/plan.ts says why that reads close to "every deal
// closes"). Off, the default, a forecast chart shows booked AR, closed
// fundraise, payroll and the plan's costs: the current-status view. On, the
// pipeline is added on top, drawn so its share is visible rather than mixed
// in. The plan's COSTS are never switched off: future payroll only exists in
// the plan, and a forecast without it reads as a business with no burn.
//
// WHY THE CONTROL IS PER-CHART BUT THE STATE IS NOT: "is pipeline in this
// line?" is a question the reader has *at* a chart, so the switch belongs in
// the chart header. But every switch drives the SAME state, owned by
// DashboardView. Letting each chart keep its own basis would let "Cash in and
// out" sit on booked money while "Cash position" includes pipeline — two panels
// on one screen quietly answering different questions, which is exactly the
// "which number am I looking at" failure this project exists to remove.

export function PlanBadge({ children }: { children: ReactNode }): JSX.Element {
  return <span className="plan-badge">{children}</span>
}

// IT NEVER MOVES WHEN FLIPPED. The label is always "Pipeline" and the count
// reads the same on and off, so nothing beside the switch changes width; the
// thumb's position carries the state (and aria-checked, for a screen reader).
// The count sits before the switch, so in a right-aligned header a count that
// differs between charts or views grows leftwards, away from the switches.
export function PipelineToggle({
  on,
  onChange,
  affected,
  total,
}: {
  on: boolean
  onChange: (next: boolean) => void
  /** Months in this chart's range whose figure the pipeline changes. */
  affected: number
  total: number
}): JSX.Element {
  // Nothing the pipeline changes in range. Flipping it would change nothing, so
  // it is disabled and says why, but it keeps its slot (no shape change between
  // views) and keeps showing the reader's choice, which still governs the
  // other charts.
  const empty = affected === 0
  return (
    <div className="plan-control">
      <span className="plan-count">
        {empty ? 'no pipeline in range' : `affects ${affected} of ${total} months`}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={empty}
        className={`plan-switch${on ? ' on' : ''}`}
        onClick={() => onChange(!on)}
        title={
          empty
            ? 'No weighted pipeline lands in these months, so there is nothing to add here. Your choice still applies to the other forecast charts.'
            : 'Add or remove weighted pipeline: deals not yet signed, at the FCF conversion rate. Applies to every forecast chart on the page, so no two charts are ever left on different bases.'
        }
      >
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
        Pipeline
      </button>
    </div>
  )
}
